import { ThemeEvents } from '@theme/events';
import { AlgoliaTemplate } from '@algolia/templates';

/**
 * @typedef {object} ThemeSettings
 * @property {boolean} [warrantyWidgetEnabled]
 * @property {boolean} [enableUpsellPopup]
 * @property {string} [popupMessage]
 * @property {boolean} [enableAddedProductImageSize]
 * @property {number} [addedProductImageSize]
 * @property {boolean} [enableAddedProductImageBorderRadius]
 * @property {number} [addedProductImageBorderRadius]
 * @property {boolean} [showAddedProductCheckmark]
 * @property {string} [addedProductCheckmarkIcon]
 * @property {string} [sliderHeadline]
 * @property {boolean} [enableUpsellSecondTab]
 * @property {boolean} [enableUpsellAlternativeProductCard]
 * @property {string} [noRecommendationsText]
 * @property {number} [productsPerView]
 */

/**
 * @typedef {object} CartUpdateData
 * @property {boolean} [didError]
 * @property {string} [source]
 * @property {string} [productId]
 * @property {string} [variantId]
 * @property {boolean} [warrantyUpsellPending]
 * @property {boolean} [warrantyAddedWithProduct]
 * @property {number} [itemCount]
 * @property {Record<string, string>} [addedProperties]
 * @property {Promise<HTMLElement | null> | null} [warrantyPopupOptions]
 * @property {string | null} [warrantyPairId]
 * @property {string | null} [warrantyVariantId]
 */

/**
 * @typedef {object} CartUpdateDetail
 * @property {CartUpdateData} [data]
 */

/**
 * @typedef {object} ProductContext
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {string} productId
 */

const STORAGE_KEY = 'upsellViewedHandles';

/** @type {ThemeSettings} */
const defaultThemeSettings = {
  enableUpsellPopup: false,
  popupMessage: '',
  enableAddedProductImageSize: false,
  addedProductImageSize: 72,
  enableAddedProductImageBorderRadius: false,
  addedProductImageBorderRadius: 0,
  showAddedProductCheckmark: false,
  addedProductCheckmarkIcon: '',
  sliderHeadline: '',
  enableUpsellSecondTab: false,
  enableUpsellAlternativeProductCard: false,
  noRecommendationsText: '',
  productsPerView: 3,
};

const sliderStateByPopup = new WeakMap();

/**
 * Element that held focus when the popup took it over, so it can be handed back
 * on close.
 *
 * @type {HTMLElement | null}
 */
let focusReturnTarget = null;

/** @returns {ThemeSettings} */
const getThemeSettings = () => window.themeSettings || defaultThemeSettings;

/** @param {unknown} value */
const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** @param {number | undefined} value */
const getAddedProductImageSize = (value) => {
  const size = Number(value) || 72;
  return Math.max(50, size);
};

/** @param {number | undefined} value */
const getAddedProductImageBorderRadius = (value) => {
  const radius = Number(value) || 0;
  return Math.max(0, radius);
};

/** @param {Record<string, string>} properties */
const buildInlineStyle = (properties) => {
  const declarations = Object.entries(properties)
    .filter(([, value]) => value)
    .map(([property, value]) => `${property}: ${value};`);

  return declarations.length ? ` style="${declarations.join(' ')}"` : '';
};

/** @param {string} iconUrl @param {string} alt */
const renderCheckmarkIcon = (iconUrl, alt) => {
  if (iconUrl) {
    return `<img class="added-product__checkmark-image" src="${escapeHtml(iconUrl)}" alt="${alt}" width="24" height="24">`;
  }

  return `
    <svg viewBox="0 0 24 24" role="img" aria-label="${alt}" focusable="false">
      <circle cx="12" cy="12" r="10.5" fill="#ffffff" stroke="#7a0" stroke-width="1.5" />
      <path d="M7.25 12.15L10.25 15.15L16.9 8.85" fill="none" stroke="#7a0" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  `;
};

/** @param {string | null | undefined} value */
const parseHandle = (value) => {
  if (!value) return '';
  const path = value.replace(window.location.origin, '');
  const match = path.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : '';
};

/** @param {string} currentHandle */
const getRecentHandles = (currentHandle) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const handles = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(handles)) return [];
    return handles.filter((handle) => handle && handle !== currentHandle).slice(0, 6);
  } catch {
    return [];
  }
};

/** @param {any} product */
const normalizeShopifyProduct = (product) => {
  const variant = product?.variants?.[0];
  const price = typeof product?.price === 'number' ? product.price : variant?.price;
  const compareAt =
    typeof product?.compare_at_price === 'number'
      ? product.compare_at_price
      : variant?.compare_at_price;

  return {
    title: product.title,
    handle: product.handle,
    image: product.featured_image || product.images?.[0] || '',
    images: product.images || [],
    price: price ? price / 100 : 0,
    compare_at_price: compareAt ? compareAt / 100 : null,
    vendor: product.vendor || '',
    objectID: String(variant?.id || product.id || ''),
    variantId: variant?.id || product.id,
    id: product.id,
    available: product.available !== false,
    inventory_available: product.available !== false,
    tags: product.tags || [],
    variants: product.variants || [],
    options: product.options || [],
    meta: { custom: {} },
  };
};

/** @param {any} product */
const isProductAvailable = (product) =>
  (product?.available ?? product?.inventory_available) === true;

/** @param {any[] | null | undefined} products */
const filterAvailableProducts = (products) =>
  Array.isArray(products) ? products.filter(isProductAvailable) : [];

/** @returns {((hit: unknown) => string) | undefined} */
const getHitTemplate = () => AlgoliaTemplate?.hitTemplate;

/** @param {HTMLElement | null} popup @param {HTMLElement | null} backdrop @param {boolean} isOpen */
const setPopupVisibility = (popup, backdrop, isOpen) => {
  if (!popup) return;
  const method = isOpen ? 'remove' : 'add';
  popup.classList[method]('is-hidden');
  backdrop?.classList[method]('is-hidden');
  document.body.classList.toggle('upsell-popup-open', isOpen);
};

/**
 * Whether focus can be handed to an element, i.e. it is still in the document
 * and not buried inside a dialog we just closed (a closed dialog is not
 * rendered, so focusing anything inside it drops focus onto `body`).
 *
 * @param {HTMLElement | null} element
 */
const canReceiveFocus = (element) => {
  if (!element?.isConnected) return false;

  const dialog = element.closest('dialog');
  return !dialog || dialog.open;
};

/**
 * Moves focus into the popup.
 *
 * The popup is `aria-modal`, but it is a plain `div`, so nothing hands focus to
 * it the way a native `<dialog>` does. Without this, the dialogs closed by
 * `closeAllDialogs` return focus to their own invoker behind the popup, and the
 * next Tab walks the page underneath instead of the popup content.
 *
 * @param {HTMLElement} popup
 */
const focusPopup = (popup) => {
  const activeElement = document.activeElement;
  focusReturnTarget =
    activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null;

  const closeButton = popup.querySelector('.upsell-popup__close');
  const target = closeButton instanceof HTMLElement ? closeButton : popup;

  // The popup itself is not focusable on its own, so make it programmatically
  // focusable when there is no close button to land on.
  if (target === popup && !popup.hasAttribute('tabindex')) {
    popup.setAttribute('tabindex', '-1');
  }

  target.focus({ preventScroll: true });
};

/** Hands focus back to whatever held it before the popup opened. */
const restorePopupFocus = () => {
  const target = focusReturnTarget;
  focusReturnTarget = null;

  if (canReceiveFocus(target)) {
    target?.focus({ preventScroll: true });
  }
};

/**
 * Closes every open native dialog in the document so the upsell popup is not
 * stacked on top of a drawer/modal that is still open.
 *
 * Dialogs owned by `dialog-component` are closed through the component, because
 * that is what releases the scroll lock it puts on `document.body`. Standalone
 * dialogs fall back to the native `close()`.
 *
 * @returns {Promise<void>}
 */
const closeAllDialogs = async () => {
  const openDialogs = Array.from(document.querySelectorAll('dialog')).filter(
    (dialog) => dialog.open
  );

  await Promise.all(
    openDialogs.map((dialog) => {
      const component = /** @type {any} */ (dialog.closest('dialog-component'));

      if (typeof component?.closeDialog === 'function') {
        return component.closeDialog();
      }

      dialog.close();
      return undefined;
    })
  );
};

/** @param {HTMLElement | null} popup @param {HTMLElement | null} backdrop */
const bindCloseHandlers = (popup, backdrop) => {
  if (!popup || popup.dataset.boundClose === 'true') return;

  const closePopup = () => {
    setPopupVisibility(popup, backdrop, false);
    restorePopupFocus();
  };
  const closeButton = popup.querySelector('.upsell-popup__close');

  if (closeButton) {
    closeButton.addEventListener('click', closePopup);
  }
  if (backdrop) {
    backdrop.addEventListener('click', closePopup);
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    // PP-987: Escape inside a dialog of the popup (the info drawer of the warranty
    // options) closes that dialog only, the popup stays open.
    if (event.defaultPrevented || popup.querySelector('dialog[open]')) return;
    closePopup();
  });

  popup.dataset.boundClose = 'true';
};

/** @param {HTMLElement} popup @param {string} activeTab */
const setActiveTab = (popup, activeTab) => {
  const state = sliderStateByPopup.get(popup);
  const slides = state?.slidesByTab?.[activeTab] || [];
  if (!state) return;

  popup.querySelectorAll('[data-upsell-tab]').forEach((tab) => {
    if (!(tab instanceof HTMLElement)) return;
    if (tab.hidden) return;
    const isActive = tab.dataset.upsellTab === activeTab;
    tab.classList.toggle('is-active', isActive);
    tab.setAttribute('aria-selected', String(isActive));
  });

  state.activeTab = activeTab;

  if (slides.length === 0) {
    resetSlider(state.sliderFrame, state.sliderEmpty);
    state.sliderFrame?.classList.add('is-hidden');
    state.sliderEmpty?.classList.remove('is-hidden');
    return;
  }

  state.sliderFrame?.classList.remove('is-hidden');
  state.sliderEmpty?.classList.add('is-hidden');
  resetSlider(state.sliderFrame, state.sliderEmpty);
  renderSlideshow(state.sliderFrame, slides);
};

/** @param {HTMLElement | null} popup */
const bindTabHandlers = (popup) => {
  if (!popup) return;
  if (popup.dataset.boundTabs === 'true') return;

  popup.addEventListener('click', (event) => {
    const target =
      event.target instanceof Element ? event.target.closest('[data-upsell-tab]') : null;
    if (!(target instanceof HTMLElement) || target.hidden || !target.dataset.upsellTab) return;
    if (popup.getAttribute('aria-busy') === 'true') return;
    if (popup.querySelector('.upsell-popup__slider--single-heading')) return;

    event.preventDefault();
    setActiveTab(popup, target.dataset.upsellTab);
  });

  popup.dataset.boundTabs = 'true';
};

/** @param {HTMLElement} popup @param {string} tabName @param {boolean} isVisible */
const setTabAvailable = (popup, tabName, isVisible) => {
  const tab = popup.querySelector(`[data-upsell-tab="${tabName}"]`);
  if (tab instanceof HTMLElement) {
    tab.classList.toggle('is-hidden', !isVisible);
    tab.hidden = !isVisible;
  }
};

/** @param {HTMLElement} popup @param {boolean} isSingleHeading */
const applySingleHeadingTypography = (popup, isSingleHeading) => {
  const primaryTab = popup.querySelector('[data-upsell-tab="primary"]');
  if (!(primaryTab instanceof HTMLElement)) return;

  const tabStyle = primaryTab.dataset.tabStyle || '';
  const tabClass = primaryTab.dataset.tabClass || '';
  const headingStyle = primaryTab.dataset.headingStyle || '';
  const headingClass = primaryTab.dataset.headingClass || '';
  const classesToReset = [...tabClass.split(' '), ...headingClass.split(' ')].filter(Boolean);

  primaryTab.setAttribute('style', isSingleHeading ? headingStyle : tabStyle);
  classesToReset.forEach((className) => primaryTab.classList.remove(className));
  (isSingleHeading ? headingClass : tabClass)
    .split(' ')
    .filter(Boolean)
    .forEach((className) => primaryTab.classList.add(className));
};

/**
 * Swaps the popup headline to the fallback text whenever the primary slideshow falls
 * back to automatic recommendations instead of curated add-ons.
 * @param {HTMLElement | null} popup @param {boolean} useFallback
 */
const applyFallbackHeadline = (popup, useFallback) => {
  if (!popup) return;
  popup.querySelectorAll('[data-fallback-headline]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.defaultHeadline === undefined) {
      el.dataset.defaultHeadline = el.textContent?.trim() || '';
    }
    el.textContent = useFallback
      ? el.dataset.fallbackHeadline || el.dataset.defaultHeadline
      : el.dataset.defaultHeadline;
  });
};

/** @param {HTMLElement | null} productBox @param {{ title?: string; image?: string }} product */
const renderAddedProduct = (productBox, { title, image }) => {
  if (!productBox) return;
  const settings = getThemeSettings();
  const message = settings.popupMessage || '';
  const safeTitle = escapeHtml(title || '');
  const safeMessage = escapeHtml(message || '');
  const imageSize = settings.enableAddedProductImageSize
    ? getAddedProductImageSize(settings.addedProductImageSize)
    : 72;
  const imageBorderRadius = settings.enableAddedProductImageBorderRadius
    ? getAddedProductImageBorderRadius(settings.addedProductImageBorderRadius)
    : null;
  const checkmarkAlt = escapeHtml(message || title || 'Added to cart');
  const imageStyle = buildInlineStyle({
    '--added-product-image-size': imageSize === 72 ? '' : `${imageSize}px`,
    '--added-product-image-border-radius':
      imageBorderRadius === null ? '' : `${imageBorderRadius}px`,
  });
  const checkmarkMarkup = settings.showAddedProductCheckmark
    ? `<span class="added-product__checkmark">${renderCheckmarkIcon(settings.addedProductCheckmarkIcon || '', checkmarkAlt)}</span>`
    : '';
  const safeImage = escapeHtml(image || '');
  const imageMarkup =
    safeImage && settings.showAddedProductCheckmark
      ? `
        <span class="added-product__media"${imageStyle}>
          <img class="added-product__image" src="${safeImage}" alt="${safeTitle}">
          ${checkmarkMarkup}
        </span>
      `
      : safeImage
        ? `<img class="added-product__image" src="${safeImage}" alt="${safeTitle}"${imageStyle}>`
        : '';

  productBox.innerHTML = `
    <div class="added-product">
      ${imageMarkup}
      <div>
        ${safeMessage ? `<p class="added-product__message">${safeMessage}</p>` : ''}
        ${safeTitle ? `<p class="added-product__title">${safeTitle}</p>` : ''}
      </div>
    </div>
  `;
};

/** @param {Element | null} script */
const parseJsonScript = (script) => {
  if (!script) return null;

  try {
    return JSON.parse(script.textContent || '');
  } catch {
    return null;
  }
};

/**
 * @typedef {{ primary: any, secondary: any }} AddonsSection
 */

/**
 * @type {Map<string, Promise<AddonsSection | null>>}
 */
const addonsSectionRequests = new Map();

/** @param {string} handle @returns {Promise<AddonsSection | null>} */
const fetchAddonsSection = (handle) => {
  if (!handle) return Promise.resolve(null);

  const existing = addonsSectionRequests.get(handle);
  if (existing) return existing;

  const request = (async () => {
    const base = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${base}products/${handle}?sections=product-addons-json`);
    // A resolved null would be cached as "this product has no add-ons". An HTTP
    // failure or a missing section is temporary, so reject and let the catch below
    // drop the entry. An empty metafield still resolves and stays cached.
    if (!response.ok) throw new Error(`Add-on section responded ${response.status}`);

    const data = await response.json();
    const html = data['product-addons-json'];
    if (!html) throw new Error('Add-on section was missing');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return {
      primary: parseJsonScript(doc.querySelector('[data-upsell-primary-products]')),
      secondary: parseJsonScript(doc.querySelector('[data-upsell-second-tab-products]')),
    };
  })();

  addonsSectionRequests.set(handle, request);
  request.catch(() => {
    if (addonsSectionRequests.get(handle) === request) addonsSectionRequests.delete(handle);
  });
  return request;
};

/**
 * @typedef {{ slides: string[], failed: boolean }} SlideLoad
 */

/**
 * @param {string} productId
 * @returns {Promise<{ products: any[] | null, failed: boolean }>}
 */
const fetchRecommendations = async (productId) => {
  if (!productId) return { products: null, failed: false };

  try {
    const base = window.Shopify?.routes?.root || '/';
    const response = await fetch(
      `${base}recommendations/products.json?product_id=${productId}&limit=6&intent=related`
    );
    if (!response.ok) return { products: null, failed: true };

    const data = await response.json();
    const products = data?.products;
    return { products: Array.isArray(products) ? products : [], failed: false };
  } catch {
    return { products: null, failed: true };
  }
};

/** @returns {string} */
const getUpsellProductCardSectionId = () => {
  const fromSlide = document
    .querySelector('[data-upsell-product-card-section-id]')
    ?.getAttribute('data-upsell-product-card-section-id');
  if (fromSlide) return fromSlide;

  const sectionElement = document.querySelector(
    '.upsell-product-card-section[id^="shopify-section-"]'
  );
  const sectionId = sectionElement?.id?.replace('shopify-section-', '');
  if (sectionId) return sectionId;

  return 'upsell_product_card';
};

/** @param {string} html */
const extractProductSlideContent = (html) => {
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const slide = doc.querySelector('.js-product-slide');
  return slide?.innerHTML?.trim() || null;
};

/**
 * A rendered section with no `.js-product-slide` is a real empty answer (PP-1011:
 * warranty add-ons get no card). An HTTP or network failure is not, and must not be
 * cached as an empty list.
 * @param {string[]} handles
 * @param {string} [sectionId]
 * @returns {Promise<SlideLoad>}
 */
const fetchProductCardSlides = async (handles, sectionId = 'last-viewed-product') => {
  if (!handles.length || !sectionId) return { slides: [], failed: false };

  const base = window.Shopify?.routes?.root || '/';
  let failed = false;
  const slides = await Promise.all(
    handles.map(async (handle) => {
      try {
        const response = await fetch(
          `${base}products/${handle}?sections=${encodeURIComponent(sectionId)}`
        );
        if (!response.ok) {
          failed = true;
          return null;
        }

        const data = await response.json();
        const html = data?.[sectionId] || Object.values(data || {})[0];
        if (typeof html !== 'string' || !html.trim()) {
          failed = true;
          return null;
        }

        return extractProductSlideContent(html);
      } catch {
        failed = true;
        return null;
      }
    })
  );

  return { slides: slides.filter(Boolean), failed };
};

/** @param {any[] | null} products @returns {Promise<SlideLoad>} */
const buildProductListSlides = async (products) => {
  const productList = filterAvailableProducts(products);
  if (productList.length === 0) return { slides: [], failed: false };

  const handles = productList
    .map((item) => item?.handle)
    .filter(Boolean)
    .slice(0, 6);

  if (!handles.length) return { slides: [], failed: false };

  if (getThemeSettings().enableUpsellAlternativeProductCard) {
    // PP-1011: the theme card decides which product gets a slide (warranty add-ons get
    // none), so an empty answer is final. The search template below cannot tell a
    // warranty add-on from a normal product in this data and must not take over.
    return fetchProductCardSlides(handles, getUpsellProductCardSectionId());
  }

  const hitTemplate = getHitTemplate();
  if (!hitTemplate) return fetchProductCardSlides(handles);

  return {
    slides: productList.map((hit) => hitTemplate(hit)).filter(Boolean),
    failed: false,
  };
};

/**
 * @param {{
 *   handle: string;
 *   productId: string;
 * }} options
 */
/** @param {string} handle @returns {Promise<SlideLoad>} */
const loadRecentSlides = (handle) => {
  const recentHandles = getRecentHandles(handle);
  const sectionId = getThemeSettings().enableUpsellAlternativeProductCard
    ? getUpsellProductCardSectionId()
    : 'last-viewed-product';
  return fetchProductCardSlides(recentHandles, sectionId);
};

/** @param {{ handle: string, productId: string }} options @returns {Promise<SlideLoad>} */
const loadFallbackSlides = async ({ handle, productId }) => {
  const recommendations = await fetchRecommendations(productId);
  if (recommendations.products?.length) {
    const built = await buildProductListSlides(
      recommendations.products.map(normalizeShopifyProduct)
    );
    if (built.slides.length) {
      return { slides: built.slides, failed: recommendations.failed || built.failed };
    }
    // Card rendering failed, so recently viewed products can fill this popup, but
    // the recommendations themselves still need another try.
    if (built.failed) {
      const recent = await loadRecentSlides(handle);
      return { slides: recent.slides, failed: true };
    }
  }

  const recent = await loadRecentSlides(handle);
  return { slides: recent.slides, failed: recommendations.failed || recent.failed };
};

/** @param {HTMLElement | null} sliderFrame @param {HTMLElement | null} sliderEmpty */
const resetSlider = (sliderFrame, sliderEmpty) => {
  const sliderHost = sliderFrame?.querySelector('[data-upsell-carousel]');
  sliderHost?.replaceChildren();
  sliderFrame?.classList.remove('is-hidden');
  sliderEmpty?.classList.add('is-hidden');
};

/** @param {number} count */
const buildTimelineScope = (count) =>
  Array.from({ length: count }, (_, index) => `--slide-${index}`).join(', ');

/** @param {string} content */
const wrapSlideContent = (content) =>
  content.includes('resource-list__item')
    ? content
    : `<div class="resource-list__item">${content}</div>`;

/** @param {string} content @param {number} index */
const buildSlideMarkup = (content, index) => `
  <slideshow-slide
    ref="slides[]"
    class="resource-list__slide upsell-popup__slide"
    aria-hidden="${index === 0 ? 'false' : 'true'}"
    style="--slideshow-timeline: --slide-${index};"
  >
    ${wrapSlideContent(content)}
  </slideshow-slide>
`;

/** @param {HTMLElement | null} sliderFrame @param {string[]} slideContents */
const renderSlideshow = (sliderFrame, slideContents) => {
  const host = sliderFrame?.querySelector('[data-upsell-carousel]');
  const template = sliderFrame
    ?.closest('.upsell-popup__slider')
    ?.querySelector('[data-upsell-slideshow-template]');
  if (!host || !(template instanceof HTMLTemplateElement)) return;

  const fragment = template.content.cloneNode(true);
  const slideshow = fragment.querySelector('slideshow-component');
  const scroller = fragment.querySelector('slideshow-slides');
  if (!slideshow || !scroller) return;

  host.replaceChildren();

  const slidesHtml = slideContents
    .map((content, index) => buildSlideMarkup(content, index))
    .join('');

  scroller.innerHTML = slidesHtml;

  const slideCount = scroller.querySelectorAll('slideshow-slide').length;
  const hostElement = host instanceof HTMLElement ? host : null;
  const columnCount = hostElement
    ? Number(getComputedStyle(hostElement).getPropertyValue('--upsell-columns').trim()) || 0
    : 0;
  const productsToShow = columnCount || window.themeSettings?.productsPerView || 3;

  const isDesktop = window.matchMedia('(min-width: 1029px)').matches;
  if (isDesktop && slideCount <= productsToShow) {
    slideshow.setAttribute('disabled', 'true');
  } else {
    slideshow.removeAttribute('disabled');
  }
  slideshow.style.setProperty('--slideshow-timeline', buildTimelineScope(slideCount));
  slideshow.setAttribute('initial-slide', '0');

  host.replaceChildren(fragment);
};

/**
 * @typedef {{ primarySlides: string[], secondarySlides: string[], isFallback: boolean }} UpsellSlides
 * @typedef {UpsellSlides & { failed: boolean }} UpsellSlidesLoad
 */

/** @param {any} products */
const availableUpsellProducts = (products) =>
  filterAvailableProducts(Array.isArray(products) ? products.map(normalizeShopifyProduct) : []);

/**
 * UZ-8535: add-on products come from the product-addons-json section; the PDP no
 * longer inlines them. Both tabs are read from that one response, and their
 * product-card sections start together.
 * Sold-out add-ons are dropped. An empty primary list falls through to recommendations.
 *
 * @param {string} handle
 * @param {string} productId
 * @returns {Promise<UpsellSlidesLoad>}
 */
const buildUpsellSlides = async (handle, productId) => {
  const settings = getThemeSettings();
  let section = null;
  let sectionFailed = false;
  if (handle) {
    try {
      section = await fetchAddonsSection(handle);
    } catch {
      // The section cache already dropped this rejection. Keep going so this
      // popup can still show a fallback, and mark the result so it is not cached.
      sectionFailed = true;
    }
  }
  const primaryProducts = availableUpsellProducts(section?.primary);
  const secondaryProducts = settings.enableUpsellSecondTab
    ? availableUpsellProducts(section?.secondary)
    : [];

  /** @type {Promise<SlideLoad>} */
  const secondaryLoad = secondaryProducts.length
    ? buildProductListSlides(secondaryProducts)
    : Promise.resolve({ slides: [], failed: false });

  if (primaryProducts.length === 0) {
    const fallback = await loadFallbackSlides({ handle, productId });
    const secondary = await secondaryLoad;
    return {
      primarySlides: fallback.slides,
      secondarySlides: secondary.slides,
      isFallback: fallback.slides.length > 0,
      failed: sectionFailed || fallback.failed || secondary.failed,
    };
  }

  const [primary, secondary] = await Promise.all([
    buildProductListSlides(primaryProducts),
    secondaryLoad,
  ]);

  return {
    primarySlides: primary.slides,
    secondarySlides: secondary.slides,
    isFallback: false,
    failed: sectionFailed || primary.failed || secondary.failed,
  };
};

/**
 * @typedef {{ promise: Promise<UpsellSlides>, slides: UpsellSlides | null }} UpsellSlidesRequest
 * @type {Map<string, UpsellSlidesRequest>}
 */
const upsellSlidesRequests = new Map();

/**
 * @param {string} handle
 * @param {string} productId
 * @returns {UpsellSlidesRequest}
 */
const loadUpsellSlides = (handle, productId) => {
  const key = `${handle}|${productId}`;
  const existing = upsellSlidesRequests.get(key);
  if (existing) return existing;

  const pending = buildUpsellSlides(handle, productId);
  /** @type {UpsellSlidesRequest} */
  const request = {
    slides: null,
    promise: pending.then((result) => {
      /** @type {UpsellSlides} */
      const slides = {
        primarySlides: result.primarySlides,
        secondarySlides: result.secondarySlides,
        isFallback: result.isFallback,
      };
      // Callers that already share this in-flight request still receive the
      // slides. A failed load leaves the map so the next add fetches again.
      request.slides = slides;
      if (result.failed && upsellSlidesRequests.get(key) === request) {
        upsellSlidesRequests.delete(key);
      }
      return slides;
    }),
  };
  upsellSlidesRequests.set(key, request);
  request.promise.catch(() => {
    if (upsellSlidesRequests.get(key) === request) upsellSlidesRequests.delete(key);
  });
  return request;
};

/**
 * Same handle the popup uses after the add, so the prefetch and the popup share one request.
 * @param {HTMLElement} productForm
 */
const productHandleFromForm = (productForm) => {
  const productCard =
    productForm.closest('[data-product-handle], product-card, .product-card') ||
    document.querySelector(`[data-product-id="${productForm.dataset.productId}"]`);
  const handleFromCard = productCard?.dataset?.productHandle || '';
  const handleFromUrl =
    parseHandle(productForm.dataset.productUrl) || parseHandle(window.location.pathname);
  const handleFromLink = parseHandle(
    productCard?.querySelector('a[href*="/products/"]')?.getAttribute('href')
  );

  return handleFromCard || handleFromUrl || handleFromLink;
};

/** @param {HTMLElement} productForm */
const prefetchUpsellForForm = (productForm) => {
  if (!getThemeSettings().enableUpsellPopup) return;

  const handle = productHandleFromForm(productForm);
  const productId = productForm.dataset.productId || '';
  if (!handle && !productId) return;

  loadUpsellSlides(handle, productId).promise.catch(() => {});
};

/**
 * PP-987: empties the warranty slot of the popup for a new add-to-cart. The returned
 * token marks that add, so tiers of an earlier add that arrive late are dropped.
 * @param {HTMLElement} popup
 * @returns {string}
 */
const resetWarrantyOptions = (popup) => {
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const slot = popup.querySelector('[data-upsell-warranty]');
  if (slot instanceof HTMLElement) {
    slot.replaceChildren();
    slot.classList.add('is-hidden');
    slot.dataset.warrantyToken = token;
  }
  return token;
};

/**
 * PP-987: places the warranty tiers of the added product inside the popup.
 * product-form.js requests them while the add runs (event data), warranty-addons.js
 * applies every toggle to the cart. Without tiers (feature off, B2C customer,
 * product without warranty) the slot stays hidden.
 *
 * The confirmation popup does not wait for the tiers. They are placed into the
 * open popup when the request that started with the add comes back. A later add
 * replaces the token, so tiers of the earlier add are dropped.
 * @param {HTMLElement} popup
 * @param {CartUpdateData | undefined} data - data of the add
 * @param {string} token
 */
const showWarrantyOptions = (popup, data, token) => {
  const slot = popup.querySelector('[data-upsell-warranty]');
  if (!(slot instanceof HTMLElement) || !data?.warrantyPopupOptions || !data.variantId) return;
  const variantId = data.variantId;

  Promise.resolve(data.warrantyPopupOptions)
    .then(async (options) => {
      if (!(options instanceof HTMLElement) || slot.dataset.warrantyToken !== token) return;

      const { bindWarrantyPopupOptions } = await import('@theme/warranty-addons');
      // A later add-to-cart may have taken over the slot while the module loaded.
      if (slot.dataset.warrantyToken !== token) return;

      bindWarrantyPopupOptions(options, {
        parentVariantId: variantId,
        pairId: data.warrantyPairId ?? null,
        warrantyVariantId: data.warrantyVariantId ?? null,
        // The product form reports the units of this add as itemCount.
        addedQuantity: Number(data.itemCount) || 1,
        addedProperties: data.addedProperties,
      });

      slot.replaceChildren(options);
      slot.classList.remove('is-hidden');
    })
    .catch((error) => {
      console.error('Warranty tiers could not be shown in the add-to-cart popup:', error);
    });
};

/** @param {CustomEvent<CartUpdateDetail>} event */
const getProductContextFromEvent = (event) => {
  if (!(event.target instanceof HTMLElement)) return null;

  const productForm = event.target.closest('product-form-component');
  if (!productForm) return null;

  const quickAdd = productForm.closest('quick-add-component');
  const productCard =
    productForm.closest('[data-product-handle], product-card, .product-card') ||
    document.querySelector(`[data-product-id="${productForm.dataset.productId}"]`);
  const handle = productHandleFromForm(productForm);
  const title =
    quickAdd?.dataset.productTitle ||
    productCard?.querySelector('[ref="productTitleLink"]')?.textContent?.trim() ||
    productCard?.querySelector('.product-card__title')?.textContent?.trim() ||
    productForm
      .closest('.product-details')
      ?.querySelector('.view-product-title a')
      ?.textContent?.trim() ||
    document.querySelector('[data-testid="sticky-product-title"]')?.textContent?.trim() ||
    '';

  const addToCartComponent = productForm.querySelector('add-to-cart-component');
  const image =
    addToCartComponent?.dataset.productVariantMedia ||
    productCard?.querySelector('img')?.getAttribute('src') ||
    '';

  const productId = event.detail?.data?.productId || productForm.dataset.productId || '';

  return {
    handle,
    title,
    image,
    productId,
  };
};

/**
 * Placeholder cards while the upsell sections are still rendering.
 * @param {HTMLElement} popup
 * @param {boolean} isPending
 */
const setSliderPending = (popup, isPending) => {
  popup.querySelector('[data-upsell-slider-loading]')?.classList.toggle('is-hidden', !isPending);
  if (isPending) {
    popup.setAttribute('aria-busy', 'true');
    popup.querySelector('[data-upsell-slider-frame]')?.classList.add('is-hidden');
    popup.querySelector('[data-upsell-slider-empty]')?.classList.add('is-hidden');
    return;
  }

  popup.removeAttribute('aria-busy');
};

/**
 * @param {HTMLElement} popup
 * @param {Element | null} sliderFrame
 * @param {Element | null} sliderEmpty
 * @param {UpsellSlides} slides
 */
const presentUpsellSlides = (popup, sliderFrame, sliderEmpty, slides) => {
  const settings = getThemeSettings();
  const { primarySlides, secondarySlides, isFallback } = slides;
  setSliderPending(popup, false);
  applyFallbackHeadline(popup, isFallback);

  const activeTab = 'primary';
  sliderStateByPopup.set(popup, {
    sliderFrame,
    sliderEmpty,
    slidesByTab: {
      primary: primarySlides,
      secondary: secondarySlides,
    },
    activeTab,
  });

  if (settings.enableUpsellSecondTab) {
    setTabAvailable(popup, 'secondary', secondarySlides.length > 0);
    setTabAvailable(popup, 'primary', primarySlides.length > 0 || secondarySlides.length === 0);
  }

  popup
    .querySelector('.upsell-popup__slider')
    ?.classList.toggle(
      'upsell-popup__slider--single-heading',
      settings.enableUpsellSecondTab && secondarySlides.length === 0
    );
  applySingleHeadingTypography(
    popup,
    settings.enableUpsellSecondTab && secondarySlides.length === 0
  );

  setActiveTab(popup, activeTab);
};

/** @param {HTMLElement} popup */
const nextPopupRequestId = (popup) => {
  const id = String(Number(popup.dataset.upsellRequest || 0) + 1);
  popup.dataset.upsellRequest = id;
  return id;
};

/**
 * Shows the popup for a product that was just added to the cart: the added
 * product, its warranty tiers and the recommendations.
 *
 * The popup opens as soon as it is called. Upsell cards that are still loading
 * fill the placeholder in afterwards. A newer add replaces an older one, and a
 * load that finishes after the popup was closed stays out of view.
 * @param {ProductContext} context
 * @param {CartUpdateData | undefined} data - data of the add (units, line properties, warranty tiers)
 */
const showPopup = async (context, data) => {
  const popup = document.getElementById('upsell-popup');
  const backdrop = document.getElementById('upsell-popup-backdrop');
  if (!popup) return;

  const requestId = nextPopupRequestId(popup);
  const isCurrentRequest = () => popup.dataset.upsellRequest === requestId;
  const sliderFrame = popup.querySelector('[data-upsell-slider-frame]');
  const sliderEmpty = popup.querySelector('[data-upsell-slider-empty]');
  const productBox = popup.querySelector('.upsell-popup__product');

  bindCloseHandlers(popup, backdrop);
  bindTabHandlers(popup);
  renderAddedProduct(productBox, context);
  resetSlider(sliderFrame, sliderEmpty);
  showWarrantyOptions(popup, data, resetWarrantyOptions(popup));

  const slidesRequest = loadUpsellSlides(context.handle, context.productId);

  await closeAllDialogs();
  if (!isCurrentRequest()) return;

  if (slidesRequest.slides) {
    presentUpsellSlides(popup, sliderFrame, sliderEmpty, slidesRequest.slides);
  } else {
    // Drop the previous add's slides so a tab click cannot reveal them while
    // this add's cards are still loading.
    sliderStateByPopup.delete(popup);
    setSliderPending(popup, true);
  }

  setPopupVisibility(popup, backdrop, true);
  focusPopup(popup);

  if (slidesRequest.slides) return;

  try {
    const slides = await slidesRequest.promise;
    if (!isCurrentRequest() || popup.classList.contains('is-hidden')) return;
    presentUpsellSlides(popup, sliderFrame, sliderEmpty, slides);
  } catch (error) {
    console.error('Upsell products could not be loaded:', error);
    if (!isCurrentRequest() || popup.classList.contains('is-hidden')) return;
    presentUpsellSlides(popup, sliderFrame, sliderEmpty, {
      primarySlides: [],
      secondarySlides: [],
      isFallback: false,
    });
  }
};

/** @param {CustomEvent<CartUpdateDetail>} event */
const handleCartUpdate = async (event) => {
  const settings = getThemeSettings();
  if (!settings.enableUpsellPopup) return;

  if (event.detail?.data?.didError) return;
  if (event.detail?.data?.source !== 'product-form-component') return;
  // PP-863/PP-906: the warranty upsell drawer takes over this add — the cart drawer
  // follows once it is answered, the recommendations popup stays out of the way.
  if (event.detail?.data?.warrantyUpsellPending === true) return;
  // PP-987: a warranty picked on the product page travels with the product. The
  // popup has nothing left to offer for that add, the cart drawer opens instead.
  if (event.detail?.data?.warrantyAddedWithProduct === true) {
    const { openCartDrawer } = await import('@theme/warranty-addons');
    openCartDrawer();
    return;
  }

  console.log('Detail', event.detail);

  const context = getProductContextFromEvent(event);
  if (!context) return;

  await showPopup(context, event.detail?.data);
};

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);

// The upsell section does not depend on the add, so start it with the submit
// instead of after /cart/add.js and /cart.js return. The capture listener runs
// before the product form handles the submit.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = form.closest('product-form-component');
    if (!(productForm instanceof HTMLElement)) return;
    prefetchUpsellForForm(productForm);
  },
  true
);

/**
 * PP-987: an add that did not go through a product form asks for the popup with
 * this event. The wishlist page does, its adds come from the wishlist app
 * (se-wishlist-page-override.js). detail.context names the added product,
 * detail.data carries what a product form reports for its add.
 */
document.addEventListener('upsell-popup:show', (event) => {
  if (!getThemeSettings().enableUpsellPopup) return;
  const { context, data } = /** @type {CustomEvent} */ (event).detail ?? {};
  if (!context?.handle) return;

  showPopup(context, data).catch((error) => {
    console.error('Add-to-cart popup could not be shown:', error);
  });
});

/**
 * PP-987: a page restored from the back/forward cache comes back with the popup of
 * its last add-to-cart still open, while the cart may have changed since (the
 * customer left through the popup buttons). That add is over, the popup closes.
 */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  const popup = document.getElementById('upsell-popup');
  const backdrop = document.getElementById('upsell-popup-backdrop');
  if (popup && !popup.classList.contains('is-hidden')) {
    setPopupVisibility(popup, backdrop, false);
    focusReturnTarget = null;
  }
});
